import { Box, InputAdornment, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useId, type ReactElement, type ReactNode, type Ref } from 'react';

import { controlHeight, radius } from '../tokens';
import { Icon } from './Icon';
import { Kbd } from './Kbd';

/**
 * Champ de saisie et selecteur (maquette « BO/Champ »).
 *
 * Ils existent pour une raison d'accessibilite autant que d'apparence : le
 * `TextField` de MUI pose son libelle **dans** le cadre et le fait flotter au
 * focus. La maquette — comme les formulaires du mobile — met le libelle
 * **au-dessus**, toujours visible : un libelle qui se deplace sous les yeux et
 * un texte d'exemple qui tient lieu de libelle sont deux causes classiques de
 * saisie dans le mauvais champ.
 *
 * Le cablage est fait une fois ici, pour tous les ecrans : `<label for>`, aide
 * et erreur reliees par `aria-describedby`, `aria-invalid` quand une erreur est
 * affichee — l'erreur **remplace** l'aide et dit quoi corriger, en toutes
 * lettres et non par la seule couleur du cadre.
 */

interface FieldFrameProps {
  readonly id: string;
  readonly labelId: string;
  readonly label: string;
  readonly required: boolean;
  readonly helpId: string;
  readonly hint: ReactNode;
  readonly error: string | undefined;
  readonly children: ReactNode;
}

const FieldFrame = ({ id, labelId, label, required, helpId, hint, error, children }: FieldFrameProps): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <Typography
        component="label"
        id={labelId}
        htmlFor={id}
        sx={{ fontSize: 13, fontWeight: 700, color: 'tokens.textStrong' }}
      >
        {label}
      </Typography>
      {/* La mention est visuelle ; les technologies d'assistance lisent `aria-required`. */}
      {required ? (
        <Typography component="span" aria-hidden sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
          obligatoire
        </Typography>
      ) : null}
    </Box>
    {children}
    {error !== undefined || hint !== undefined ? (
      <Typography
        id={helpId}
        component="div"
        sx={{ fontSize: 12, lineHeight: '16px', color: error === undefined ? 'tokens.textMuted' : 'error.main' }}
      >
        {error ?? hint}
      </Typography>
    ) : null}
  </Box>
);

interface CommonFieldProps {
  readonly label: string;
  /** Aide permanente sous le champ. Remplacee par `error` quand il y en a une. */
  readonly hint?: ReactNode;
  /** Ce qui ne va pas, et quoi corriger. Sa seule presence met le champ en erreur. */
  readonly error?: string | undefined;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
}

export interface FieldProps extends CommonFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Perte du focus : un champ a brouillon local y valide ou abandonne sa saisie. */
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly type?: 'text' | 'email' | 'search' | 'password' | 'date' | 'datetime-local';
  readonly name?: string;
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly multiline?: boolean;
  readonly minRows?: number;
  /**
   * `L` : le grand champ de 56 px des ecrans de connexion et de recuperation
   * (rayon 14, anneau de focus de 4 px). `M` partout ailleurs.
   */
  readonly size?: 'M' | 'L';
  /** Icone de tete, decorative (une loupe pour une recherche). */
  readonly icon?: LucideIcon;
  /** Raccourci clavier qui donne le focus au champ, affiche en fin de cadre (« ⌘K »). */
  readonly shortcut?: string;
  /**
   * Controle pose en fin de cadre (la bascule « afficher le mot de passe »).
   * Interactif, donc a nommer par lui-meme ; prend la place du raccourci.
   */
  readonly trailing?: ReactNode;
  readonly inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>;
}

/**
 * Grand champ : 56 px, rayon 14 et, au focus, un anneau de 4 px a 12 % de la
 * couleur d'action en plus du contour plein du theme. L'anneau seul ne suffirait
 * pas (12 % reste sous 3:1) : c'est le contour qui identifie le focus.
 */
const largeFieldSx = (theme: Theme) => ({
  minHeight: controlHeight.L,
  borderRadius: `${String(radius.field)}px`,
  fontSize: 15,
  '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` },
});

export const Field = ({
  label,
  hint,
  error,
  required = false,
  disabled = false,
  id,
  value,
  onChange,
  onBlur,
  placeholder,
  type = 'text',
  name,
  autoComplete,
  autoFocus,
  multiline = false,
  minRows,
  size = 'M',
  icon,
  shortcut,
  trailing,
  inputRef,
}: FieldProps): ReactElement => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const helpId = `${inputId}-aide`;
  const hasHelp = error !== undefined || hint !== undefined;

  return (
    <FieldFrame
      id={inputId}
      labelId={`${inputId}-libelle`}
      label={label}
      required={required}
      helpId={helpId}
      hint={hint}
      error={error}
    >
      <OutlinedInput
        id={inputId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        error={error !== undefined}
        multiline={multiline}
        minRows={minRows}
        inputRef={inputRef}
        size="small"
        fullWidth
        inputProps={{
          'aria-describedby': hasHelp ? helpId : undefined,
          'aria-invalid': error !== undefined,
          'aria-required': required,
          'aria-keyshortcuts': shortcut,
        }}
        startAdornment={
          icon === undefined ? undefined : (
            <InputAdornment position="start" sx={{ color: 'tokens.textMuted' }}>
              <Icon icon={icon} size={size === 'L' ? 20 : 18} />
            </InputAdornment>
          )
        }
        endAdornment={
          trailing !== undefined ? (
            <InputAdornment position="end">{trailing}</InputAdornment>
          ) : shortcut === undefined ? undefined : (
            <InputAdornment position="end" aria-hidden>
              <Kbd>{shortcut}</Kbd>
            </InputAdornment>
          )
        }
        sx={multiline ? undefined : size === 'L' ? largeFieldSx : { minHeight: controlHeight.M }}
      />
    </FieldFrame>
  );
};

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectFieldProps extends CommonFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
}

/** Chevron Lucide a la place de la fleche pleine de MUI ; MUI le positionne. */
const SelectChevron = ({ className }: { className?: string }): ReactElement => (
  <Box component="span" className={className} sx={{ display: 'inline-flex', color: 'tokens.textMuted' }}>
    <Icon icon={ChevronDown} size={18} />
  </Box>
);

export const SelectField = ({
  label,
  hint,
  error,
  required = false,
  disabled = false,
  id,
  value,
  onChange,
  options,
}: SelectFieldProps): ReactElement => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const labelId = `${inputId}-libelle`;
  const helpId = `${inputId}-aide`;
  const hasHelp = error !== undefined || hint !== undefined;

  return (
    <FieldFrame
      id={inputId}
      labelId={labelId}
      label={label}
      required={required}
      helpId={helpId}
      hint={hint}
      error={error}
    >
      <Select
        labelId={labelId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        disabled={disabled}
        error={error !== undefined}
        IconComponent={SelectChevron}
        input={<OutlinedInput id={inputId} size="small" fullWidth sx={{ minHeight: controlHeight.M }} />}
        SelectDisplayProps={{
          'aria-describedby': hasHelp ? helpId : undefined,
          'aria-invalid': error !== undefined,
          'aria-required': required,
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FieldFrame>
  );
};
